import { Account } from 'model/IChat';
import { ExperienceSource } from 'model/IExperience';
import Trip from 'model/ITrip';
import Logger from 'utils/Logger';
import { SearchAdsInformation } from '~/cordovaPlugins/Diagnostics';
import { AssignedExperiment } from '~/experiments';

const whenIdle = window.requestIdleCallback
  ? window.requestIdleCallback
  : (callback) => setTimeout(callback, 100);

export class BusinessEvent {
  type = 'BusinessEvent';
  traffic_type?: string;
  account_is_host?: boolean;
  account_id?: string;
  anonymous_device_id?: string;
  experiments?: AssignedExperiment[];

  protected getEventData: () => any;
}

export class PageView extends BusinessEvent {
  type = 'PageView';
  title: string;
  name: string;
  location: string;
  path: string;
  search: string;
}

export class AppleSearchAdsInit extends BusinessEvent {
  type = 'AppleSearchAdsInit';
  data: Partial<SearchAdsInformation>;
}

export class SearchPageViewed extends PageView {
  type = 'SearchPageViewed';
  city?: string;
  category?: string;
}

export class Click extends BusinessEvent {
  type = 'Click';
  target?: string;
  buttonText?: string;
  experience_id?: string;
  city?: string;
  country?: string;
  rating?: string | number;
  action?: string;
  time?: string;
  date?: string;
  host?: Partial<Account>;
  languages?: string;
  specialties?: string | string[];
  nrOfAdults?: number;
  nrOfChildren?: number;
  keywords?: string;
  subCategories?: string;
  transportMode?: string;
  price?: string;
  durationFilter?: string;
  familyFriendly?: string;
  instantConfirmation?: string;
  categories?: string;
  date_from?: string;
  date_to?: string;
}

export class LocalPickerSubmitted extends BusinessEvent {
  type = 'LocalPickerSubmitted';
  experience_id?: string;
  selected?: string;
  options?: string[];
}

export class ExperienceReviewUpvoteChange extends BusinessEvent {
  type = 'ExperienceReviewUpvoteChange';
  experience_id?: string;
  review_id?: string;
  action: 'add_upvote' | 'remove_upvote';
}

export class CookieConsentUpdate extends BusinessEvent {
  type = 'CookieConsentUpdate';
  functional: boolean;
  analytics: boolean;
  advertisement: boolean;
  personalised_ads: boolean;
  social: boolean;
  location: boolean;
}

export class TripPaid extends BusinessEvent {
  type = 'TripPaid';
  trip?: Partial<Trip>;
}

export class ActionPointModalButtonClicked extends BusinessEvent {
  type = 'ActionPointModalButtonClicked';
  pathname?: string;
}

export class ExperienceRelatedEvent extends BusinessEvent {
  type = 'ExperienceRelatedEvent';
  experience: Partial<ExperienceSource>;
  nrOfAdults?: number;
  nrOfChildren?: number;
  host?: Partial<Account>;
  date?: string;
  time?: string;
  position?: number;
  analyticsListName?: string;
}

export class ExperiencePageViewed extends ExperienceRelatedEvent {
  type = 'ExperiencePageViewed';
}

export class ExperienceCardViewed extends ExperienceRelatedEvent {
  type = 'ExperienceCardViewed';
}

export class ExperienceCardClicked extends ExperienceRelatedEvent {
  type = 'ExperienceCardClicked';
}

export class FAQViewed extends ExperienceRelatedEvent {
  type = 'FAQViewed';
}

export class ReadMoreClicked extends ExperienceRelatedEvent {
  type = 'ReadMoreClicked';
}

export class LandingPageRelatedEvent extends BusinessEvent {
  type = 'LandingPageRelatedEvent';
  buttonText?: string;
}

export class LandingPageAboutWithlocalsOpen extends LandingPageRelatedEvent {
  type = 'LandingPageAboutWithlocalsOpen';
}

export class ContactHostFormEvent extends BusinessEvent {
  type = 'ContactHostFormRelatedEvent';
  experience?: Partial<ExperienceSource>;
  host?: Partial<Account>;
  enabledForm?: string;
  source?: string;
}

export class ContactHostFormViewed extends ContactHostFormEvent {
  type = 'ContactHostFormViewed';
}

export class ContactHostFormSent extends ContactHostFormEvent {
  type = 'ContactHostFormSent';
  nrOfAdults?: number;
  nrOfChildren?: number;
  date?: string;
  time?: string;
  duration?: number;
}

export class ContactHostFormSentClick extends ContactHostFormSent {
  type = 'ContactHostFormSentClick';
  buttonText?: string;
}

export class ContactOtherHostsClick extends ContactHostFormSent {
  type = 'ContactOtherHostsClick';
  buttonText?: string;
}

export class CheckoutProcessEvent extends BusinessEvent {
  type = 'CheckoutProcessEvent';
  trip?: Partial<Trip>;
  experience?: Partial<ExperienceSource>;
  nrOfAdults?: number;
  nrOfChildren?: number;
  host?: Partial<Account>;
  date?: string;
  time?: string;
  variant?: string;
}

export class HostSelected extends CheckoutProcessEvent {
  type = 'HostSelected';
}

export class DateSelected extends CheckoutProcessEvent {
  type = 'DateSelected';
}

export class TimeSelected extends CheckoutProcessEvent {
  type = 'TimeSelected';
}

export class NumberOfGuestsSelected extends CheckoutProcessEvent {
  type = 'NumberOfGuestsSelected';
}

export class PaymentOptionSelected extends CheckoutProcessEvent {
  type = 'PaymentOptionSelected';
}

export class CheckoutPageViewed extends CheckoutProcessEvent {
  type = 'CheckoutPageViewed';

  getEventData = () => ({
    experience_id: this.trip?.experience_id,
    trip_title: this.trip?.title,
    trip_id: this.trip?.id,
    guest_id: this?.trip?.guest_id,
  });
}

export class BookButtonClicked extends CheckoutProcessEvent {
  type = 'BookButtonClicked';
  buttonText?: string;
}

export class PaymentFormType extends CheckoutProcessEvent {
  type = 'PaymentFormType';
}

export class TripRelatedEvent extends BusinessEvent {
  type = 'TripRelatedEvent';
  trip?: Partial<Trip>;
  payment_id?: string;
}

export class BookingConfirmed extends TripRelatedEvent {
  type = 'BookingConfirmed';

  getEventData = () => ({
    trip_id: this.trip?.id,
    trip_title: this.trip?.title,
    experience_id: this.trip?.experience_id,
    amount: this.trip?.payment?.required_payment,
    payment_id: this?.trip?.payment?.payment?.id,
    guest_id: this?.trip?.guest_id,
    area: this?.trip?.area,
    country: this?.trip?.country,
    currency: this?.trip?.payment?.currency,
    coupon_code: this?.trip?.coupon_code,
  });
}

interface PushNotificationEventData {
  action: 'open' | 'receive';
  template_name?: string;
  language?: string;
  link?: string;
  notification_id?: string;
  anonymous_device_id?: string;
  target_id?: string;
  target_type?: string;
}

export class PushNotificationEvent extends BusinessEvent {
  type = 'PushNotificationEvent';
  action: 'open' | 'receive';
  template_name?: string;
  language?: string;
  link?: string;
  notification_id?: string;
  target_id?: string;
  target_type?: string;

  constructor(data: PushNotificationEventData) {
    super();
    Object.assign(this, data);
  }
}

export type BusinessEventHandler = (event: BusinessEvent) => void;

export class BusinessEventSource {
  private _listeners: BusinessEventHandler[] = [];
  register = (listener: BusinessEventHandler) => {
    this._listeners.push(listener);
  };
  emit = (event: BusinessEvent | PageView) => {
    this._listeners.forEach((listener) =>
      whenIdle(() => {
        try {
          listener(event);
        } catch (error) {
          Logger.error(
            '[trekker] Error in business event handler',
            event,
            error,
          );
        }
      }),
    );
  };
  emitSync = (event: BusinessEvent | PageView) => {
    this._listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        Logger.error('[trekker] Error in business event handler', event, error);
      }
    });
  };
}
